import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DirectionsCarRoundedIcon from '@mui/icons-material/DirectionsCarRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import SupervisorAccountRoundedIcon from '@mui/icons-material/SupervisorAccountRounded';
import { formatDate } from '../../../helpers/FormateDate';
import { VEHICLE_ASSIGNMENT_MODE } from './MissionAssignVehicleMode';

const COLORS = {
  primary: '#0878f9',
  primaryDark: '#0b162f',
  secondaryText: '#47567a',
  border: '#d8e0ec',
  selectedBackground: '#eef7ff',
  iconBackground: '#e7f3ff',
};

const AssignmentOption = ({
  value,
  selected,
  icon,
  title,
  description,
  onSelect,
}) => (
  <FormControlLabel
    value={value}
    onClick={onSelect}
    control={<Radio color="primary" />}
    sx={{
      alignItems: 'center',
      backgroundColor: selected ? COLORS.selectedBackground : '#fff',
      border: `${selected ? 2 : 1}px solid ${selected ? COLORS.primary : COLORS.border}`,
      borderRadius: 2,
      boxSizing: 'border-box',
      cursor: 'pointer',
      m: 0,
      minHeight: 88,
      px: { xs: 1.25, sm: 2 },
      py: 1.25,
      width: '100%',
      '& .MuiFormControlLabel-label': {
        width: '100%',
      },
    }}
    label={(
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 2, width: '100%' }}>
        <Box
          sx={{
            alignItems: 'center',
            backgroundColor: selected ? '#dceeff' : '#f0f3f7',
            borderRadius: '50%',
            color: selected ? COLORS.primary : '#40506c',
            display: 'flex',
            flex: '0 0 auto',
            height: 52,
            justifyContent: 'center',
            width: 52,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: COLORS.primaryDark, fontSize: 16, fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography sx={{ color: COLORS.secondaryText, fontSize: 13.5, fontWeight: 400 }}>
            {description}
          </Typography>
        </Box>
      </Box>
    )}
  />
);

const MissionDetailRow = ({ icon, label, value, valueColor }) => (
  <Box
    sx={{
      alignItems: 'start',
      display: 'grid',
      gap: { xs: 0.5, sm: 1 },
      gridTemplateColumns: { xs: '24px 1fr', sm: '24px 190px minmax(0, 1fr)' },
      py: 0.6,
    }}
  >
    <Box sx={{ color: '#6b7280', display: 'flex', pt: 0.15 }}>{icon}</Box>
    <Typography sx={{ color: COLORS.secondaryText, fontSize: 14.5 }}>
      {label}
    </Typography>
    <Typography
      sx={{
        color: valueColor || COLORS.secondaryText,
        fontSize: 14.5,
        gridColumn: { xs: '2', sm: '3' },
        overflowWrap: 'anywhere',
      }}
    >
      {value || '—'}
    </Typography>
  </Box>
);

const CarPoolingConfirm = (props) => {
  const {
    carpoolingDialog,
    carpoolingMissions,
    carpoolingLoading,
    handleCloseCarpoolingDialog,
    handleConfirmCarpooling,
    handleChangeVehicleMode,
    getCaptainName,
    classes,
  } = props;
  const isConfirmation = carpoolingDialog.action === 'confirm';
  const registration = carpoolingDialog?.vehicle?.vhc_imm || '';
  const captainName = getCaptainName(carpoolingDialog.vehicle);
  const missionDetailsTitle = carpoolingMissions.length > 1
    ? 'Détails des missions concernées'
    : 'Détails de la mission concernée';

  return (
    <Dialog
      open={carpoolingDialog.open}
      onClose={handleCloseCarpoolingDialog}
      aria-labelledby="carpooling-dialog-title"
      aria-describedby="carpooling-dialog-description"
      fullWidth
      maxWidth="md"
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(3px)',
          backgroundColor: 'rgba(15, 23, 42, 0.55)',
        },
      }}
      PaperProps={{
        className: classes.dialogPaperCompact,
        sx: {
          background: '#fff',
          border: '1px solid #d8e0ec',
          borderRadius: 3,
          maxWidth: 900,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        id="carpooling-dialog-title"
        sx={{ alignItems: 'center', display: 'flex', gap: 2, px: { xs: 2, sm: 3 }, py: 2.25 }}
      >
        <Box
          sx={{
            alignItems: 'center',
            backgroundColor: COLORS.iconBackground,
            borderRadius: '50%',
            color: '#0767cf',
            display: 'flex',
            flex: '0 0 auto',
            height: 56,
            justifyContent: 'center',
            width: 56,
          }}
        >
          <DirectionsCarRoundedIcon fontSize="large" />
        </Box>
        <Typography
          component="span"
          sx={{ color: COLORS.primaryDark, flexGrow: 1, fontSize: { xs: 19, sm: 23 }, fontWeight: 600 }}
        >
          {isConfirmation
            ? "Utilisation d'une voiture déjà en mission"
            : 'Détails du covoiturage'}
        </Typography>
        <IconButton
          aria-label="Fermer"
          onClick={handleCloseCarpoolingDialog}
          sx={{ color: '#6b7280' }}
        >
          <CloseRoundedIcon fontSize="large" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: 2.5, pt: '0 !important' }}>
        <Box
          id="carpooling-dialog-description"
          sx={{
            alignItems: 'flex-start',
            backgroundColor: '#edf8ff',
            border: '1px solid #cce9ff',
            borderRadius: 2,
            color: COLORS.secondaryText,
            display: 'flex',
            gap: 2,
            mb: 2.5,
            px: 2.25,
            py: 2,
          }}
        >
          <InfoRoundedIcon sx={{ color: COLORS.primary, flex: '0 0 auto', fontSize: 34 }} />
          <Typography sx={{ fontSize: { xs: 13, sm: 14 }, fontWeight: 400, lineHeight: 1.45 }}>
            {registration ? 'La voiture ' : 'Cette voiture'}
            {registration && <strong>{registration}</strong>}
            {' est déjà associée à une ou plusieurs missions.'}
            {isConfirmation && (
              <>
                <br />
                Choisissez comment elle sera utilisée pour cette mission.
              </>
            )}
          </Typography>
        </Box>

        {isConfirmation && (
          <RadioGroup
            aria-label="Mode d'utilisation du véhicule"
            value={carpoolingDialog.mode}
            onChange={(event) => handleChangeVehicleMode(event.target.value)}
            sx={{ gap: 1.5 }}
          >
            <AssignmentOption
              value={VEHICLE_ASSIGNMENT_MODE.CARPOOLING}
              selected={carpoolingDialog.mode === VEHICLE_ASSIGNMENT_MODE.CARPOOLING}
              icon={<GroupRoundedIcon fontSize="large" />}
              title="Covoiturage"
              description="Transport, chef de bord et carburant partagés."
              onSelect={() => handleChangeVehicleMode(VEHICLE_ASSIGNMENT_MODE.CARPOOLING)}
            />
            <AssignmentOption
              value={VEHICLE_ASSIGNMENT_MODE.SEPARATE_TRIP}
              selected={carpoolingDialog.mode === VEHICLE_ASSIGNMENT_MODE.SEPARATE_TRIP}
              icon={<DirectionsCarRoundedIcon fontSize="large" />}
              title="Course différente"
              description="Nouveau transport et carburant calculé séparément."
              onSelect={() => handleChangeVehicleMode(VEHICLE_ASSIGNMENT_MODE.SEPARATE_TRIP)}
            />
          </RadioGroup>
        )}

        <Box sx={{ borderTop: '1px solid #e1e7ef', mt: 2.5, pt: 2.5 }}>
          <Box sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 2, overflow: 'hidden' }}>
            <Box
              sx={{
                alignItems: 'center',
                backgroundColor: '#fbfcfe',
                borderBottom: `1px solid ${COLORS.border}`,
                display: 'flex',
                gap: 1.25,
                px: 2.25,
                py: 1.5,
              }}
            >
              <AssignmentRoundedIcon sx={{ color: '#6b7280' }} />
              <Typography sx={{ color: COLORS.primaryDark, fontSize: 15, fontWeight: 600 }}>
                {missionDetailsTitle}
              </Typography>
            </Box>

            {carpoolingLoading && (
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5, justifyContent: 'center', py: 4 }}>
                <CircularProgress size={22} />
                <Typography color="text.secondary">Chargement des détails...</Typography>
              </Box>
            )}

            {!carpoolingLoading && carpoolingMissions.length === 0 && (
              <Typography color="text.secondary" sx={{ px: 2.25, py: 3, textAlign: 'center' }}>
                Aucune mission trouvée.
              </Typography>
            )}

            {!carpoolingLoading && carpoolingMissions.map((mission, missionIndex) => (
              <Box
                key={mission?.mission_planning_id || mission?.reference || missionIndex}
                sx={{
                  borderTop: missionIndex > 0 ? `1px solid ${COLORS.border}` : 'none',
                  px: { xs: 1.5, sm: 2.25 },
                  py: 1.5,
                }}
              >
                <MissionDetailRow
                  icon={<AssignmentRoundedIcon fontSize="small" />}
                  label="Référence mission"
                  value={mission?.reference}
                  valueColor={COLORS.primary}
                />
                <MissionDetailRow
                  icon={<AssignmentRoundedIcon fontSize="small" />}
                  label="Objet de la mission"
                  value={mission?.mission_title}
                />
                <MissionDetailRow
                  icon={<CalendarTodayRoundedIcon fontSize="small" />}
                  label="Date de début"
                  value={mission?.date_begin ? formatDate(mission.date_begin) : ''}
                />
                <MissionDetailRow
                  icon={<CalendarTodayRoundedIcon fontSize="small" />}
                  label="Date de fin"
                  value={mission?.date_end ? formatDate(mission.date_end) : ''}
                />
                <MissionDetailRow
                  icon={<PlaceRoundedIcon fontSize="small" />}
                  label="Destinations"
                  value={mission?.lieu}
                />
                <MissionDetailRow
                  icon={<GroupRoundedIcon fontSize="small" />}
                  label="Missionnaires"
                  value={Array.isArray(mission?.users)
                    ? mission.users.map((user) => user.name).join(', ')
                    : ''}
                />
                <MissionDetailRow
                  icon={<SupervisorAccountRoundedIcon fontSize="small" />}
                  label="Chef de bord déjà assigné"
                  value={captainName}
                  valueColor="#149223"
                />
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: `1px solid ${COLORS.border}`,
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: 1.5,
          justifyContent: 'flex-end',
          px: { xs: 2, sm: 3 },
          py: 2,
        }}
      >
        <Button
          onClick={handleCloseCarpoolingDialog}
          color="error"
          variant="outlined"
          className={classes.actionButton}
          sx={{ fontWeight: 500, minWidth: 130, width: { xs: '100%', sm: 'auto' } }}
        >
          {isConfirmation ? 'Annuler' : 'Fermer'}
        </Button>
        {isConfirmation && (
          <Button
            onClick={handleConfirmCarpooling}
            variant="contained"
            color="primary"
            autoFocus
            className={classes.actionButton}
            sx={{ fontWeight: 500, minWidth: 210, width: { xs: '100%', sm: 'auto' } }}
          >
            Confirmer le choix
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CarPoolingConfirm;
